<template>
  <v-dialog :value="open" width="508" @click:outside="handleClose" @keydown.esc="handleClose">
    <v-card
      class="pid-card"
      role="dialog"
      aria-modal="true"
      aria-labelledby="pid-title"
      :data-testid="config.testId"
    >
      <button
        type="button"
        class="pid-close"
        aria-label="Close"
        data-testid="pid-close"
        @click="handleClose"
      >
        <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
          <path
            d="M11.3337 4.66602L4.66699 11.3327M4.66699 4.66602L11.3337 11.3327"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
        </svg>
      </button>

      <div class="pid-header">
        <span class="pid-icon" aria-hidden="true">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
            <path
              d="M21 15v1.2c0 1.68 0 2.52-.327 3.162a3 3 0 0 1-1.311 1.311C18.72 21 17.88 21 16.2 21H7.8c-1.68 0-2.52 0-3.162-.327a3 3 0 0 1-1.311-1.311C3 18.72 3 17.88 3 16.2V15m14-7-5-5m0 0L7 8m5-5v12"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
          </svg>
        </span>
        <h2 id="pid-title" class="pid-title" data-testid="pid-title">{{ stateTitle }}</h2>
        <p class="pid-description" data-testid="pid-description">{{ stateDescription }}</p>
      </div>

      <!-- ── PICKER: drop zone / selected file / inline error ── -->
      <template v-if="phase === 'entry'">
        <div class="pid-body">
          <button
            v-if="!selectedFile"
            type="button"
            class="pid-dropzone"
            :class="{ 'pid-dropzone--dragover': isDragging }"
            :aria-describedby="visibleError ? 'pid-error' : undefined"
            data-testid="pid-dropzone"
            @click="openFilePicker"
            @dragover.prevent="isDragging = true"
            @dragleave.prevent="isDragging = false"
            @drop.prevent="handleDrop"
          >
            <span class="pid-dropzone__icon" aria-hidden="true">
              <img :src="uploadCloudSrc" alt="" width="20" height="20" />
            </span>
            <span class="pid-dropzone__action">
              <span class="pid-dropzone__link">Click to upload</span>
              <span class="pid-dropzone__or">or drag and drop</span>
            </span>
            <span class="pid-dropzone__hint">{{ config.strings.dropzoneHint }}</span>
          </button>

          <UploadedFileCardWithViewer
            v-else
            :file-name="selectedFile.name"
            :file-type="config.mimeType"
            :type-label="selectedFileLabel"
            :is-preview-open="!config.canOpenInViewer"
            @open-viewer="openSelectedFile"
          />

          <p
            v-if="visibleError"
            id="pid-error"
            class="pid-error"
            role="alert"
            data-testid="pid-error"
          >
            {{ visibleError }}
          </p>
        </div>

        <div class="pid-footer">
          <button
            type="button"
            class="pid-btn pid-btn--secondary"
            data-testid="pid-cancel"
            @click="handleClose"
          >
            Cancel
          </button>
          <button
            type="button"
            class="pid-btn pid-btn--primary"
            :class="{ 'pid-btn--disabled': !canSubmit }"
            :disabled="!canSubmit"
            :aria-busy="isStarting"
            data-testid="pid-submit"
            @click="handleSubmit"
          >
            {{ isStarting ? "Importing…" : "Import" }}
          </button>
        </div>
      </template>

      <!-- ── STARTING / IMPORTING: progress ── -->
      <template v-else-if="phase === 'starting' || phase === 'importing'">
        <div class="pid-body">
          <div
            class="pid-progress"
            role="progressbar"
            aria-valuemin="0"
            :aria-valuemax="progressTotal || 0"
            :aria-valuenow="progressDone"
            :aria-valuetext="progressCount"
            data-testid="pid-progress"
          >
            <div
              class="pid-progress__fill"
              :style="{ width: `${progressPercent}%` }"
              data-testid="pid-progress-fill"
            />
          </div>
          <div class="pid-progress-meta">
            <span class="pid-progress-status" data-testid="pid-status">{{ progressStatus }}</span>
            <span v-if="phase === 'importing'" class="pid-progress-count" data-testid="pid-count">
              {{ progressCount }}
            </span>
          </div>
        </div>

        <div class="pid-footer">
          <button
            type="button"
            class="pid-btn pid-btn--secondary"
            data-testid="pid-cancel"
            @click="handleClose"
          >
            Cancel
          </button>
        </div>
      </template>

      <!-- ── FAILED ── -->
      <template v-else-if="phase === 'failed'">
        <div class="pid-body">
          <p class="pid-error" role="alert" data-testid="pid-error">{{ failedErrorMessage }}</p>
        </div>

        <div class="pid-footer">
          <button
            type="button"
            class="pid-btn pid-btn--secondary"
            data-testid="pid-cancel"
            @click="handleClose"
          >
            Cancel
          </button>
          <button
            type="button"
            class="pid-btn pid-btn--primary"
            :disabled="isStarting"
            data-testid="pid-retry"
            @click="retry"
          >
            Try again
          </button>
        </div>
      </template>

      <input
        ref="fileInput"
        type="file"
        class="pid-input"
        :accept="config.accept"
        tabindex="-1"
        :aria-label="config.strings.inputLabel"
        data-testid="pid-file-input"
        @change="handleFileInput"
      />
    </v-card>
  </v-dialog>
</template>

<script setup>
import { computed, ref, watch } from "vue";

import UploadedFileCardWithViewer from "@components/UploadedFileCardWithViewer/UploadedFileCardWithViewer.vue";
import { useScribeImportJob } from "@components/utils/useScribeImportJob";

/**
 * ER-12400 — FileImportDialog (shared by PdfImportDialog and WordImportDialog).
 *
 * Untitled-UI modal that collects one file (click or drag-and-drop), checks it client-side,
 * uploads it as multipart and then follows the background job (202 `job_id` + status endpoint)
 * through the Starting / Importing / Failed states. Success emits `success` with the result so
 * the parent can populate the editor.
 *
 * Everything that differs between file types is passed in `config`: the accepted extension and
 * MIME type, the strings, the client validation and the error-code-to-message mapping. The thin
 * wrappers (PdfImportDialog, WordImportDialog) own those configs.
 *
 * Like ScribeImportDialog, the parent injects the two API calls (`startImport`, `pollStatus`)
 * so the dialog stays free of `@/api`/store imports and is storybook-safe.
 */
const props = defineProps({
  /** Boolean that controls dialog visibility. */
  open: {
    type: Boolean,
    default: false,
  },
  accountId: {
    type: [Number, String],
    default: null,
  },
  responsibilityId: {
    type: [Number, String],
    default: null,
  },
  checkpointId: {
    type: [Number, String],
    default: null,
  },
  /** POST the multipart import; resolves 202 `{ data: { job_id } }`, rejects 422 with an import error code. */
  startImport: {
    type: Function,
    default: null,
  },
  /** GET the job status; resolves `{ data: { status, progress } }`. */
  pollStatus: {
    type: Function,
    default: null,
  },
  /**
   * File-type configuration:
   * - `testId`, `accept`, `mimeType`, `fileKind` (card label prefix, e.g. "PDF"),
   *   `canOpenInViewer` (show the card's "Open in viewer" button)
   * - `strings`: title, description, dropzoneHint, inputLabel, busyTitle, startingDescription,
   *   importingDescription, startingStatus, importingStatus, failedTitle, failedDescription
   * - `multipleFilesMessage`, `formatFileSize(bytes)`
   * - `validateFile(file)` -> error code or "", `getErrorMessage(code)`,
   *   `getStartErrorMessage(error)` (rejected start), `getJobFailureMessage(data)` (failed job)
   */
  config: {
    type: Object,
    required: true,
  },
});

const emit = defineEmits(["success", "close"]);

const uploadCloudSrc = require("@components/assets/upload-cloud-02.svg");

const fileInput = ref(null);
const selectedFile = ref(null);
const isDragging = ref(false);
const clientValidationError = ref("");

const job = useScribeImportJob({
  startImport: (formData) =>
    props.startImport?.(props.accountId, props.responsibilityId, props.checkpointId, formData),
  pollStatus: (jobId) =>
    props.pollStatus?.(props.accountId, props.responsibilityId, props.checkpointId, jobId),
  onSucceeded: (result) => {
    emit("success", result);
    emit("close");
  },
  buildPayload: (file) => {
    const formData = new FormData();
    formData.append("file", file);
    return formData;
  },
  resolveErrorMessage: (error) => props.config.getStartErrorMessage(error),
});

const phase = job.phase;
const isStarting = job.isStarting;
const progressDone = computed(() => job.progress.value.done);
const progressTotal = computed(() => job.progress.value.total);
const progressPercent = computed(() => {
  const total = progressTotal.value;
  if (!total) {
    return 0;
  }
  return Math.round((progressDone.value / total) * 100);
});
const progressCount = computed(() => `${progressDone.value} of ${progressTotal.value}`);

const canSubmit = computed(() => Boolean(selectedFile.value) && !isStarting.value);

const visibleError = computed(() => clientValidationError.value || job.entryError.value || "");

const selectedFileLabel = computed(
  () => `${props.config.fileKind} · ${props.config.formatFileSize(selectedFile.value?.size)}`
);

const failedErrorMessage = computed(
  () => job.entryError.value || props.config.getJobFailureMessage(job.failureData.value)
);

const stateTitle = computed(() => {
  const { strings } = props.config;
  if (phase.value === "failed") {
    return strings.failedTitle;
  }
  if (phase.value === "starting" || phase.value === "importing") {
    return strings.busyTitle;
  }
  return strings.title;
});

const stateDescription = computed(() => {
  const { strings } = props.config;
  if (phase.value === "failed") {
    return strings.failedDescription;
  }
  if (phase.value === "importing") {
    return strings.importingDescription;
  }
  if (phase.value === "starting") {
    return strings.startingDescription;
  }
  return strings.description;
});

const progressStatus = computed(() =>
  phase.value === "importing"
    ? props.config.strings.importingStatus
    : props.config.strings.startingStatus
);

watch(
  () => props.open,
  (isOpen) => {
    // Reset the picker + inline errors + job phase on every open so a stale file,
    // error or half-finished job never leaks across opens.
    if (isOpen) {
      selectedFile.value = null;
      clientValidationError.value = "";
      isDragging.value = false;
      job.reset();
    } else {
      job.cancel();
    }
  }
);

// A rejected upload (422) leaves nothing worth retrying: drop the file so another is chosen.
watch(
  () => job.entryError.value,
  (message) => {
    if (message && phase.value === "entry") {
      selectedFile.value = null;
    }
  }
);

function openFilePicker() {
  fileInput.value?.click();
}

function selectFiles(files) {
  const list = Array.from(files || []);
  if (!list.length) {
    return;
  }
  job.reset();
  selectedFile.value = null;
  if (list.length > 1) {
    clientValidationError.value = props.config.multipleFilesMessage;
    return;
  }
  const code = props.config.validateFile(list[0]);
  if (code) {
    clientValidationError.value = props.config.getErrorMessage(code);
    return;
  }
  clientValidationError.value = "";
  selectedFile.value = list[0];
}

function handleFileInput(event) {
  selectFiles(event.target.files);
  // Allow choosing the same file again after an error.
  event.target.value = "";
}

function handleDrop(event) {
  isDragging.value = false;
  selectFiles(event.dataTransfer?.files);
}

function openSelectedFile() {
  if (!selectedFile.value || typeof URL?.createObjectURL !== "function") {
    return;
  }
  const url = URL.createObjectURL(selectedFile.value);
  window.open(url, "_blank", "noopener");
  setTimeout(() => URL.revokeObjectURL(url), 60000);
}

function handleSubmit() {
  if (!canSubmit.value) {
    return;
  }
  clientValidationError.value = "";
  job.start(selectedFile.value);
}

function handleClose() {
  job.cancel();
  emit("close");
}

function retry() {
  job.retry();
}
</script>

<style scoped>
.pid-card {
  position: relative;
  overflow: hidden;
  background-color: #ffffff;
  background-image: url("@components/assets/backgrounds/background_popup_icon.svg");
  background-size: 216px 216px;
  background-position: 0 0;
  border: 1px solid #e4e7ec;
  border-radius: 12px;
}

.pid-close {
  position: absolute;
  top: 12px;
  right: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 44px;
  height: 44px;
  padding: 0;
  background: #ffffff;
  border: none;
  border-radius: 8px;
  color: #667085;
  cursor: pointer;
}

.pid-close:hover {
  background: #f9fafb;
  color: #344054;
}

.pid-header {
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 24px 24px 20px;
}

.pid-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 48px;
  height: 48px;
  margin-bottom: 12px;
  background: #f9f5ff;
  border-radius: 9999px;
  color: #7f56d9;
}

.pid-title {
  margin: 0;
  color: #101828;
  font-size: 18px;
  font-weight: 600;
  line-height: 28px;
}

.pid-description {
  margin: 0;
  color: #475467;
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
}

.pid-body {
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 0 29px;
}

.pid-dropzone {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
  width: 100%;
  padding: 16px 24px;
  background: #ffffff;
  border: 1px solid #eaecf0;
  border-radius: 12px;
  font-family: inherit;
  cursor: pointer;
}

.pid-dropzone--dragover {
  background: #f9f5ff;
  border-color: #7f56d9;
}

.pid-dropzone__icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  margin-bottom: 8px;
  background: #ffffff;
  border: 1px solid #eaecf0;
  border-radius: 8px;
  box-shadow: 0 1px 2px rgba(16, 24, 40, 0.05);
}

.pid-dropzone__action {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
}

.pid-dropzone__link {
  color: #6941c6;
  font-size: 14px;
  font-weight: 600;
  line-height: 20px;
}

.pid-dropzone__or {
  color: #475467;
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
}

.pid-dropzone__hint {
  color: #475467;
  font-size: 12px;
  font-weight: 400;
  line-height: 18px;
}

.pid-error {
  margin: 0;
  color: #d92d20;
  font-size: 14px;
  line-height: 20px;
}

.pid-progress {
  width: 100%;
  height: 8px;
  background: #eaecf0;
  border-radius: 4px;
  overflow: hidden;
}

.pid-progress__fill {
  height: 100%;
  background: #7f56d9;
  border-radius: 4px;
  transition: width 0.2s ease;
}

.pid-progress-meta {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
}

.pid-progress-status {
  color: #475467;
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
}

.pid-progress-count {
  color: #344054;
  font-size: 14px;
  font-weight: 500;
  line-height: 20px;
  white-space: nowrap;
}

.pid-footer {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  margin-top: 32px;
  padding: 24px 24px 24px;
  border-top: 1px solid #eaecf0;
}

.pid-btn {
  box-sizing: border-box;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  height: 44px;
  min-width: 88px;
  padding: 0 16px;
  border-radius: 8px;
  font-family: inherit;
  font-size: 14px;
  font-weight: 600;
  line-height: 20px;
  cursor: pointer;
}

.pid-btn--primary {
  background: #7f56d9;
  border: 1px solid #7f56d9;
  color: #ffffff;
  box-shadow: 0 1px 2px rgba(16, 24, 40, 0.05);
}

.pid-btn--secondary {
  background: #ffffff;
  border: 1px solid #d0d5dd;
  color: #344054;
}

/* Disabled Import: #667085 on #f2f4f7 = 4.51:1 (the Scribe #98a2b3 grey fails WCAG AA). */
.pid-btn--disabled,
.pid-btn:disabled {
  background: #f2f4f7;
  border: none;
  color: #667085;
  box-shadow: none;
  cursor: not-allowed;
}

.pid-dropzone:focus-visible,
.pid-close:focus-visible,
.pid-btn:focus-visible {
  outline: 2px solid #7f56d9;
  outline-offset: 1px;
}

.pid-input {
  position: absolute;
  width: 1px;
  height: 1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
}
</style>
